import { BadgeCheck, Ban, CircleDot } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { CityPlaceRow, PlaceStage } from "./types";

export const PAGE_SIZE = 25;

export function pct(done: number, total: number) {
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

export function numberOrNull(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function textOrNull(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

export function neighborhoodList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (value && typeof value === "object") return Object.values(value as Record<string, unknown>).flatMap((item) => Array.isArray(item) ? item.map(String) : [String(item)]);
  if (typeof value === "string") return value.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
}

export function formatDate(value: unknown) {
  if (!value) return "Not recorded";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function stageForPlace(place: CityPlaceRow): PlaceStage {
  if (place.isPublished) return "live";
  if (place.cutReason || place.hygieneVerdict === "cut") return "cut";
  if (place.hygieneVerdict) return "hygiene";
  if (place.format || place.cuisine.length) return "classified";
  if (place.isSurvivor) return "resolved";
  return "discovery";
}

// Friendly names for pipeline stages — the ONLY strings humans should see.
// Canonical stage ids stay the wire values (POST /admin/api/runs, stage_runs
// rows); legacy ids from old runs are covered too so history stays readable.
export const STAGE_LABELS: Record<string, string> = {
  extract: "Find places",
  reviews: "Collect reviews",
  "reviews-enqueue": "Request reviews",
  "reviews-collect": "Fetch reviews",
  classify: "Fill in details",
  hygiene: "Quality check",
  assemble: "Publish",
  republish: "Republish",
  media: "Photos & videos",
  "media-audit": "Photos & videos",
};

export const STAGE_DESCRIPTIONS: Record<string, string> = {
  extract: "Sweep the whole city for candidate places",
  reviews: "Request + download review text for every place",
  "reviews-enqueue": "Ask the data provider to prepare reviews",
  "reviews-collect": "Download the prepared reviews",
  classify: "AI fills cuisine, price and amenities, and writes the description",
  hygiene: "Scan reviews for food-safety and scam signals (pass / flag / cut)",
  assemble: "Combine everything, compute scores and put places live",
  republish: "Rebuild live data from what's already processed",
  media: "Make sure each live place has a cover, photos and clips",
  "media-audit": "Make sure each live place has a cover, photos and clips",
};

export function stageLabel(stage: unknown): string {
  const key = String(stage ?? "");
  return STAGE_LABELS[key] ?? key;
}

export function stateTone(state: string) {
  const normalized = state.toLowerCase();
  if (["running", "queued", "waiting"].includes(normalized)) return "bg-amber-500";
  if (["done", "complete", "completed", "success"].includes(normalized)) return "bg-emerald-500";
  if (["error", "errored", "failed", "terminated", "cancelled"].includes(normalized)) return "bg-red-500";
  return "bg-muted-foreground/50";
}

export function formatCell(value: unknown) {
  if (value == null) return <span className="text-muted-foreground">empty</span>;
  if (Array.isArray(value)) return <Badge variant="outline">{value.length} items</Badge>;
  if (typeof value === "object") return <Badge variant="outline">{Object.keys(value as Record<string, unknown>).length} fields</Badge>;
  if (typeof value === "boolean") return value ? <BadgeCheck className="size-4 text-emerald-500" /> : <Ban className="size-4 text-muted-foreground" />;
  return <span className="line-clamp-1 max-w-72">{String(value)}</span>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="grid gap-1.5"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;
}

export function SwitchField({ label, checked, onChange, disabled = false }: { label: string; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

export function NativeSelect({ value, options, onChange }: { value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return (
    <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50" value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Unassigned</option>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}

export function TagEditor({ label, values, suggestions, onChange }: { label: string; values: string[]; suggestions: readonly string[]; onChange: (values: string[]) => void }) {
  const add = (value: string) => {
    const next = value.trim();
    if (next && !values.includes(next)) onChange([...values, next]);
  };
  return (
    <div className="grid gap-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-1 rounded-md border p-2">
        {values.map((value) => (
          <Badge key={value} variant="secondary" className="gap-1">
            {value}
            <button type="button" onClick={() => onChange(values.filter((item) => item !== value))}>×</button>
          </Badge>
        ))}
        <Input className="h-6 w-44 border-0 px-1 shadow-none focus-visible:ring-0" onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            add(event.currentTarget.value);
            event.currentTarget.value = "";
          }
        }} placeholder="Add tag" />
      </div>
      {!!suggestions.length && <div className="flex flex-wrap gap-1">{suggestions.filter((item) => !values.includes(item)).slice(0, 14).map((item) => <button key={item} type="button" className="rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted" onClick={() => add(item)}>{item}</button>)}</div>}
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="grid place-items-center rounded-lg border p-10 text-center"><div><CircleDot className="mx-auto mb-3 text-muted-foreground" /><h2 className="font-medium">{title}</h2><p className="mt-1 text-sm text-muted-foreground">{description}</p></div></div>;
}

export function PageError({ error }: { error: unknown }) {
  return <div className="m-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>;
}

export function StructuredRecord({ title, value, compact = false }: { title: string; value: unknown; compact?: boolean }) {
  const gap = compact ? "gap-1.5" : "gap-2";
  return (
    <div className={`grid ${gap}`}>
      <h3 className="text-sm font-medium">{title}</h3>
      <div className={`grid ${gap}`}>
        {value == null && <div className="text-sm text-muted-foreground">No values recorded.</div>}
        {Array.isArray(value) && value.map((item, index) => <StructuredMini key={index} row={typeof item === "object" && item ? item as Record<string, unknown> : { value: item }} compact={compact} />)}
        {value !== null && value !== undefined && typeof value === "object" && !Array.isArray(value) && <StructuredMini row={value as Record<string, unknown>} compact={compact} />}
        {typeof value !== "object" && value != null && <div>{String(value)}</div>}
      </div>
    </div>
  );
}

export function StructuredMini({ row, fields, compact = false }: { row: Record<string, unknown>; fields?: string[]; compact?: boolean }) {
  const entries = (fields ?? Object.keys(row)).filter((key) => row[key] !== undefined).slice(0, 12);
  return (
    <div className={`grid gap-1 rounded-md border text-xs ${compact ? "p-1.5" : "p-2"}`}>
      {entries.map((key) => (
        <div key={key} className="grid grid-cols-[120px_minmax(0,1fr)] gap-2">
          <span className="text-muted-foreground">{key}</span>
          <span className="min-w-0 truncate">{simpleValue(row[key])}</span>
        </div>
      ))}
    </div>
  );
}

function simpleValue(value: unknown) {
  if (value == null) return "empty";
  if (Array.isArray(value)) return `${value.length} items`;
  if (typeof value === "object") return `${Object.keys(value as Record<string, unknown>).length} fields`;
  return String(value);
}
